import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress, Divider, Link, Typography } from '@mui/material';
import { SBSection } from '@dndModels';
import { DataType } from '@dndModels/Types';
import API from '@utilities/smart_api';
import DisplayStringWithReplacementsRecursive from './display/DisplayStringWithReplacementsRecursive';

const api = API.getInstance();

interface ContentsListProps {
  sections: SBSection[];
  // The Contents section itself, which is left out of its own list.
  contentsId: string;
  onNavigate: (sectionId: string) => void;
}

/**
 * What a Contents section shows: every section in the book (with subsections),
 * each name a link to that section.
 */
function ContentsList({ sections, contentsId, onNavigate }: ContentsListProps) {
  const listed = sections.filter(s => s.true_id !== contentsId);
  if (listed.length === 0) return null;
  return (
    <ul style={{ margin: 0, paddingLeft: 20 }}>
      {listed.map(s => (
        <li key={s.true_id}>
          <Link component="button" variant="body1" style={{ textAlign: 'left' }} onClick={() => onNavigate(s.true_id)}>
            {s.name || 'Untitled Section'}
          </Link>
          <ContentsList sections={s.subsections} contentsId={contentsId} onNavigate={onNavigate} />
        </li>
      ))}
    </ul>
  );
}

interface SBSectionContentViewerProps {
  section: SBSection;
  inSinglePage?: boolean;
  onAllLoaded?: (sectionId: string) => void;
  // The source book's top-level sections and how to go to one, for Contents sections.
  allSections?: SBSection[];
  onNavigate?: (sectionId: string) => void;
  // For special sections that show the source book's objects in place, like Weapon Table.
  sourceBookId?: string;
}

export default function SBSectionContentViewer({
  section,
  inSinglePage = false,
  onAllLoaded,
  allSections = [],
  onNavigate,
  sourceBookId,
}: SBSectionContentViewerProps) {
  const [loadedSection, setLoadedSection] = useState<SBSection | null>(
    section.loaded ? section : null
  );
  const [loading, setLoading] = useState(!section.loaded);

  const childCount = inSinglePage ? section.subsections.length : 0;

  // Everything needed for the "all done" check lives in refs so nothing
  // goes stale across renders.
  const loadedChildIds = useRef<Set<string>>(new Set());
  const selfDoneRef = useRef(section.loaded);
  const onAllLoadedRef = useRef(onAllLoaded);
  onAllLoadedRef.current = onAllLoaded; // always current, no dep issues

  // ---------------------------------------------------------------------------
  // Load own content
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (section.loaded) {
      setLoadedSection(section);
      setLoading(false);
      selfDoneRef.current = true;
      tryFireAllLoaded();
      return;
    }
    if (section._id === '') {
      setLoadedSection(section);
      setLoading(false);
      selfDoneRef.current = true;
      tryFireAllLoaded();
      return;
    }
    setLoading(true);
    // if (section.name.includes("Using")) {
    //   debugger;
    // }
    api.getFullObject2<SBSection>({ data_type: 'sb_section', id: section._id }).then(from_db => {
      if (from_db) {
        section.copy(from_db);
        setLoadedSection(section.clone());
      }
      selfDoneRef.current = true;
      setLoading(false);
      tryFireAllLoaded();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  // ---------------------------------------------------------------------------
  // "All loaded" check — only uses refs, never goes stale
  // ---------------------------------------------------------------------------

  function tryFireAllLoaded() {
    if (!selfDoneRef.current) return;
    if (loadedChildIds.current.size < childCount) return;
    onAllLoadedRef.current?.(section.true_id);
  }

  function handleChildLoaded(childId: string) {
    loadedChildIds.current.add(childId);
    tryFireAllLoaded();
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const renderContent = () => {
    if (loading || !loadedSection) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888', marginBottom: 16 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading…</Typography>
        </div>
      );
    }
    const isContents = loadedSection.special_type === 'Contents';
    // Special sections that show one table of the source book's objects, and the token that builds it.
    const tableToken = loadedSection.special_type === 'Weapon Table' ? 'weapon_table'
      : loadedSection.special_type === 'Armor Table' ? 'armor_table'
      : null;
    return (
      <>
        {loadedSection.content && (
          <DisplayStringWithReplacementsRecursive display_string={loadedSection.content} />
        )}
        {isContents && onNavigate && (
          <ContentsList sections={allSections} contentsId={section.true_id} onNavigate={onNavigate} />
        )}
        {tableToken && sourceBookId && (
          <DisplayStringWithReplacementsRecursive display_string={`{${tableToken}.${sourceBookId}}`} />
        )}
      </>
    );
  };

  // console.log(section.true_id);
  return (
    <div id={`sb-section-${section.true_id}`}>
      {renderContent()}
      <Divider style={{ marginTop: 8, marginBottom: 16 }} />
      {inSinglePage && section.subsections.map(sub => {
        return (
          <SBSectionContentViewer
            key={sub.true_id}
            section={sub}
            inSinglePage
            onAllLoaded={handleChildLoaded}
            allSections={allSections}
            onNavigate={onNavigate}
            sourceBookId={sourceBookId}
          />
        );
      })}
    </div>
  );
}